import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Checkbox,
  TextField,
  CircularProgress,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { IconCross } from "../../../utils/icons";
import axiosInstant from "../../../api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ShortlistFormModal from "../../admin/Shortlists/ShortlistFormModal";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedExpert: any;
}

export function AddExperttoShortlist({
  open,
  onClose,
  selectedExpert,
}: Props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [shortlists, setShortlists] = useState<any>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loadingShortlistId, setLoadingShortlistId] = useState<number | null>(null);
  const [optimisticUpdates, setOptimisticUpdates] = useState<{ [key: number]: boolean }>({});
  const navigate = useNavigate();

  const fetchShortlists = React.useCallback(async () => {
    if (!open) return;
    try {
      const response = await axiosInstant.get("/shortlists", {
        params: { search: searchQuery },
      });
      setShortlists(response.data?.rows || []);
    } catch (error) {
      console.error("Error fetching shortlists:", error);
    }
  }, [open, searchQuery]);

  useEffect(() => {
    fetchShortlists();
  }, [fetchShortlists, selectedExpert]);

  const handleToggleShortlist = async (shortlistId: number, isChecked: boolean) => {
    setOptimisticUpdates(prev => ({
      ...prev,
      [shortlistId]: isChecked
    }));

    setLoadingShortlistId(shortlistId);

    try {
      switch (isChecked) {
        case true:
          await axiosInstant.post(`/shortlists/${shortlistId}/experts`, {
            userId: selectedExpert.userid,
          });
          toast.success("Expert added to shortlist");
          break;

        case false:
          await axiosInstant.delete(`/shortlists/${shortlistId}/experts/${selectedExpert.userid}`);
          toast.success("Expert removed from shortlist");
          break;
      }
    } catch (error) {
      console.error("Error updating shortlist:", error);
      toast.error(`Failed to update shortlist`);
      setOptimisticUpdates(prev => ({
        ...prev,
        [shortlistId]: !isChecked
      }));
    } finally {
      setLoadingShortlistId(null);
      await fetchShortlists();
      setOptimisticUpdates(prev => {
        const newState = { ...prev };
        delete newState[shortlistId];
        return newState;
      });
    }
  };

  const handleCreateNewShortlist = () => {
    setIsCreateModalOpen(true);
  };

  return (
    <>
      <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
        <DialogTitle className="!border-b !border-border-token gap-6">
          <Box className="flex items-center justify-between">
            <Typography variant="h5">
              Shortlists
            </Typography>
            <TextField
              variant="outlined"
              placeholder="Search"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: "50%" }}
            />
            <Box className="p-2 cursor-pointer" onClick={onClose}>
              <IconCross />
            </Box>
          </Box>
        </DialogTitle>

        <Box className="px-6 pt-4 flex">
          <Typography
            variant="body1"
            sx={{
              color: "var(--accent-text)",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 450,
              lineHeight: "132%",
              cursor: "pointer",
            }}
            onClick={handleCreateNewShortlist}
          >
            + Create new shortlist
          </Typography>
        </Box>

        <DialogContent className="!p-0">
          {shortlists.length > 0 ? (
            shortlists.map((shortlist: any) => {
              const isAlreadyInShortlist = shortlist.experts.some(
                (expert: any) => expert.userid === selectedExpert?.userid
              );

              const isChecked = optimisticUpdates.hasOwnProperty(shortlist.id)
                ? optimisticUpdates[shortlist.id]
                : isAlreadyInShortlist;

              return (
                <Box
                  key={shortlist.id}
                  className="flex w-full py-1 px-4 items-center justify-between cursor-pointer hover:bg-surface-2 rounded-lg"
                >
                  <Box className="flex items-center">
                    {loadingShortlistId === shortlist.id ? (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: "var(--accent)",
                          margin: "8px",
                        }}
                      />
                    ) : (
                      <Checkbox
                        size="medium"
                        checked={isChecked}
                        onChange={(e) =>
                          handleToggleShortlist(shortlist.id, e.target.checked)
                        }
                        sx={{
                          color: "var(--accent)",
                          "&.Mui-checked": {
                            color: "var(--accent)",
                          },
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        color: "var(--text)",
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 450,
                        lineHeight: "132%",
                      }}
                    >
                      {shortlist.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "var(--text-2)",
                      fontSize: "11px",
                      fontStyle: "normal",
                      fontWeight: 450,
                      lineHeight: "normal",
                      display: "flex",
                      padding: "4px 8px",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "4px",
                      borderRadius: "24px",
                      background: "var(--surface-2)",
                    }}
                  >
                    {shortlist.experts.length} Experts
                  </Typography>
                </Box>
              );
            })
          ) : (
            <Typography className="text-center text-text-2 py-4">
              No shortlists found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <ShortlistFormModal
        mode="create"
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        onShortlistCreated={() => { }}
      />
    </>
  );
}
